import React, { useMemo, useState } from "react";
import { ChevronDown, Minus, Plus, Save, Scale } from "lucide-react";
import { AppCard } from "@/components/daily/CompanionAppUi";
import {
  adjustMacro,
  evaluateBodyMassGoal,
} from "@/components/daily/bodyMassEngine";
import {
  loadRoutineSettings,
  saveRoutineSettings,
} from "@/components/daily/routineSettings";

const emptyProfile = () => ({
  enabled: false,
  weight: "",
  weightUnit: "lb",
  height: "",
  heightUnit: "cm",
  direction: "maintain",
  targetWeight: "",
  targetDate: "",
  activity: "light",
  currentCalories: "",
  currentProtein: "",
  currentCarbs: "",
  currentFat: "",
  targetProtein: "",
  targetCarbs: "",
  targetFat: "",
  dietNotes: "",
  professionalPlan: false,
  energyEffect: "unsure",
  irritabilityEffect: "unsure",
  recoveryEffect: "unsure",
});

const fieldClass =
  "mt-2 block w-full rounded-xl border border-black/10 bg-white p-3 text-sm forr-text-ink";

const MacroAdjuster = ({ label, value, onMinus, onPlus }) => (
  <div className="rounded-2xl p-3 forr-surface-tint">
    <div className="text-xs font-medium forr-text-ink">{label}</div>
    <div className="mt-3 flex items-center justify-between gap-2">
      <button
        type="button"
        onClick={onMinus}
        className="flex h-9 w-9 items-center justify-center rounded-full forr-btn-secondary"
        aria-label={`Decrease ${label} by 5 grams`}
      >
        <Minus size={15} />
      </button>
      <div className="text-center">
        <div className="font-display text-2xl forr-text-ink">{Number(value) || 0}g</div>
        <div className="text-[9px] uppercase tracking-[0.14em] forr-text-mute">
          working target
        </div>
      </div>
      <button
        type="button"
        onClick={onPlus}
        className="flex h-9 w-9 items-center justify-center rounded-full forr-btn-secondary"
        aria-label={`Increase ${label} by 5 grams`}
      >
        <Plus size={15} />
      </button>
    </div>
  </div>
);

const BodyMassNutritionModule = ({ identity }) => {
  const initialSettings = useMemo(() => loadRoutineSettings() || {}, []);
  const [profile, setProfile] = useState(
    () => ({ ...emptyProfile(), ...(initialSettings.bodyMassNutrition || {}) }),
  );
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const result = useMemo(
    () => evaluateBodyMassGoal(profile, identity || {}),
    [identity, profile],
  );

  const set = (key, value) => {
    setSaved(false);
    setProfile((current) => ({ ...current, [key]: value }));
  };

  const changeMacro = (macro, grams) => {
    setSaved(false);
    setProfile((current) => adjustMacro(current, macro, grams));
  };

  const initializeSuggestedMacros = () => {
    if (!result?.suggestedMacros) return;
    setProfile((current) => ({
      ...current,
      targetProtein: result.suggestedMacros.protein,
      targetCarbs: result.suggestedMacros.carbs,
      targetFat: result.suggestedMacros.fat,
    }));
    setSaved(false);
  };

  const save = () => {
    const current = loadRoutineSettings() || {};
    const next = saveRoutineSettings({ ...current, bodyMassNutrition: profile });
    setProfile((existing) => ({ ...existing, ...(next.bodyMassNutrition || {}) }));
    setSaved(true);
  };

  return (
    <AppCard className="mt-4" data-testid="body-mass-nutrition-module">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div>
          <div className="forr-overline">Body-mass support</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">
            Nutrition, macros & body-mass support
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed forr-text-soft">
            Plan long-term body-mass and nutrition targets here. APOS keeps those goals separate from the current nervous-system state match and uses today’s readiness, energy, recovery, schedule, and physical capacity when matching immediate output.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 forr-text-teal-deep">
          <Scale size={22} />
          <ChevronDown
            size={19}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {!open && profile.enabled && (
        <div className="mt-4 flex flex-wrap gap-2 text-xs forr-text-mute">
          <span className="rounded-full px-3 py-1.5 forr-surface-tint">
            {profile.direction === "performance"
              ? "Performance / body composition"
              : profile.direction.charAt(0).toUpperCase() + profile.direction.slice(1)}
          </span>
          {(profile.targetProtein || result?.selectedMacros?.protein) && (
            <span className="rounded-full px-3 py-1.5 forr-surface-tint">
              P {Number(profile.targetProtein) || result?.selectedMacros?.protein || 0}g
            </span>
          )}
          {(profile.targetCarbs || result?.selectedMacros?.carbs) && (
            <span className="rounded-full px-3 py-1.5 forr-surface-tint">
              C {Number(profile.targetCarbs) || result?.selectedMacros?.carbs || 0}g
            </span>
          )}
          {(profile.targetFat || result?.selectedMacros?.fat) && (
            <span className="rounded-full px-3 py-1.5 forr-surface-tint">
              F {Number(profile.targetFat) || result?.selectedMacros?.fat || 0}g
            </span>
          )}
        </div>
      )}

      {open && (
        <div className="mt-5 border-t pt-5" style={{ borderColor: "var(--forr-line-soft)" }}>
          <label className="flex items-start gap-3 rounded-2xl p-4 forr-surface-tint">
            <input
              type="checkbox"
              checked={Boolean(profile.enabled)}
              onChange={(event) => set("enabled", event.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium forr-text-ink">
                Use body-mass & nutrition support
              </span>
              <span className="mt-1 block text-xs leading-relaxed forr-text-mute">
                Turn this on when calories, macros, body-mass direction, or a nutrition plan should be available to Plan, Calendar, and Companion. Turning it off does not change your assessment or response history.
              </span>
            </span>
          </label>

          {profile.enabled && (
            <div className="mt-5 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="text-sm forr-text-soft">
                  Current body mass
                  <div className="mt-2 flex gap-2">
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      value={profile.weight}
                      onChange={(event) => set("weight", event.target.value)}
                      className="block min-w-0 flex-1 rounded-xl border border-black/10 bg-white p-3 forr-text-ink"
                    />
                    <select
                      value={profile.weightUnit}
                      onChange={(event) => set("weightUnit", event.target.value)}
                      className="rounded-xl border border-black/10 bg-white px-2 forr-text-ink"
                    >
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </label>
                <label className="text-sm forr-text-soft">
                  Height
                  <div className="mt-2 flex gap-2">
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      value={profile.height}
                      onChange={(event) => set("height", event.target.value)}
                      className="block min-w-0 flex-1 rounded-xl border border-black/10 bg-white p-3 forr-text-ink"
                    />
                    <select
                      value={profile.heightUnit}
                      onChange={(event) => set("heightUnit", event.target.value)}
                      className="rounded-xl border border-black/10 bg-white px-2 forr-text-ink"
                    >
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                </label>
                <label className="text-sm forr-text-soft">
                  Goal direction
                  <select
                    value={profile.direction}
                    onChange={(event) => set("direction", event.target.value)}
                    className={fieldClass}
                  >
                    <option value="maintain">Maintain</option>
                    <option value="gain">Gain</option>
                    <option value="reduce">Reduce</option>
                    <option value="performance">Performance / body composition</option>
                  </select>
                </label>
                <label className="text-sm forr-text-soft">
                  Typical activity
                  <select
                    value={profile.activity}
                    onChange={(event) => set("activity", event.target.value)}
                    className={fieldClass}
                  >
                    <option value="low">Low</option>
                    <option value="light">Light</option>
                    <option value="moderate">Moderate</option>
                    <option value="high">High</option>
                    <option value="veryHigh">Very high</option>
                  </select>
                </label>
              </div>

              {profile.direction !== "maintain" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-sm forr-text-soft">
                    Desired body mass
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      value={profile.targetWeight}
                      onChange={(event) => set("targetWeight", event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                  <label className="text-sm forr-text-soft">
                    Desired date
                    <input
                      type="date"
                      value={profile.targetDate}
                      onChange={(event) => set("targetDate", event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                </div>
              )}

              <div>
                <div className="text-sm font-medium forr-text-ink">Current intake or macros</div>
                <p className="mt-1 text-xs leading-relaxed forr-text-mute">
                  Enter calories, macros, a diet pattern, or only the information you actually know.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ["currentCalories", "Calories / day"],
                    ["currentProtein", "Protein g"],
                    ["currentCarbs", "Carbohydrate g"],
                    ["currentFat", "Fat g"],
                  ].map(([key, label]) => (
                    <label key={key} className="text-xs forr-text-soft">
                      {label}
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={profile[key]}
                        onChange={(event) => set(key, event.target.value)}
                        className={fieldClass}
                      />
                    </label>
                  ))}
                </div>
                <label className="mt-3 block text-xs forr-text-soft">
                  Diet / meal-plan notes
                  <textarea
                    rows="3"
                    value={profile.dietNotes}
                    onChange={(event) => set("dietNotes", event.target.value)}
                    placeholder="Foods, meal structure, tolerances, restrictions, or a professional plan you want Companion to consider."
                    className={fieldClass}
                  />
                </label>
                <label className="mt-3 flex items-center gap-2 text-xs forr-text-soft">
                  <input
                    type="checkbox"
                    checked={Boolean(profile.professionalPlan)}
                    onChange={(event) => set("professionalPlan", event.target.checked)}
                  />
                  This is based on a physician/dietitian or other professional nutrition plan.
                </label>
              </div>

              {result?.ready && (
                <div className="rounded-[22px] p-5 forr-panel-information">
                  <div className="forr-overline">Working estimate</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div>
                      <div className="font-display text-2xl forr-text-ink">
                        {result.maintenanceCalories}
                      </div>
                      <div className="text-[10px] forr-text-mute">
                        estimated maintenance kcal/day
                      </div>
                    </div>
                    <div>
                      <div className="font-display text-2xl forr-text-ink">
                        {result.workingCalories ?? "—"}
                      </div>
                      <div className="text-[10px] forr-text-mute">working goal estimate</div>
                    </div>
                    <div>
                      <div className="font-display text-2xl forr-text-ink">
                        {result.energyGapPercent > 0 ? "+" : ""}
                        {result.energyGapPercent}%
                      </div>
                      <div className="text-[10px] forr-text-mute">
                        estimated intake/demand difference
                      </div>
                    </div>
                  </div>
                  {result.staged && (
                    <p className="mt-4 text-xs leading-relaxed forr-text-soft">
                      Your longer-term target is larger than one working cycle. APOS stages the current calculation near {result.stagedTargetWeight} {profile.weightUnit} rather than increasing the calorie change to chase the full target at once.
                    </p>
                  )}
                  {result.aggressive && (
                    <p className="mt-3 text-xs leading-relaxed forr-text-soft">
                      The requested timeline implies an unusually aggressive energy change. APOS limits the automatic estimate and treats the longer-term target as staged rather than promising that rate.
                    </p>
                  )}
                </div>
              )}

              {result?.errors?.length > 0 && (
                <div className="rounded-2xl p-4" style={{ backgroundColor: "#f8e5e2" }}>
                  {result.errors.map((error) => (
                    <p
                      key={error}
                      className="text-xs leading-relaxed"
                      style={{ color: "#843c3c" }}
                    >
                      {error}
                    </p>
                  ))}
                </div>
              )}

              {result?.ready && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium forr-text-ink">Working macros</div>
                      <p className="mt-1 text-xs forr-text-mute">
                        Adjust any target by 5 g. These totals also appear with the calendar above.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={initializeSuggestedMacros}
                      className="rounded-full px-4 py-2 text-xs forr-btn-secondary"
                    >
                      Start from simple estimate
                    </button>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <MacroAdjuster
                      label="Protein"
                      value={profile.targetProtein || result.suggestedMacros.protein}
                      onMinus={() => changeMacro("protein", -5)}
                      onPlus={() => changeMacro("protein", 5)}
                    />
                    <MacroAdjuster
                      label="Carbohydrate"
                      value={profile.targetCarbs || result.suggestedMacros.carbs}
                      onMinus={() => changeMacro("carbs", -5)}
                      onPlus={() => changeMacro("carbs", 5)}
                    />
                    <MacroAdjuster
                      label="Fat"
                      value={profile.targetFat || result.suggestedMacros.fat}
                      onMinus={() => changeMacro("fat", -5)}
                      onPlus={() => changeMacro("fat", 5)}
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={save}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium forr-btn-primary"
              >
                <Save size={16} /> Save nutrition/body-mass plan
              </button>
              {saved && (
                <span className="ml-3 text-xs forr-text-teal-deep">
                  Saved to your APOS profile and calendar context.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </AppCard>
  );
};

export default BodyMassNutritionModule;
